<template>
    <div>
        <b-modal
                id="floating-upload-form-modal"
                ref="modal"
                title="Add Global Metadata"
                no-close-on-backdrop
                no-close-on-esc
                hide-header-close
                v-model="show"
                @shown="handleModalVisible"
                @hidden="shown = false"
                @close="handleApply">

            <template v-slot:modal-footer>
                <b-button variant="primary" class="btn-rounded px-3" @click="handleApply">APPLY</b-button>
            </template>

            <b-row v-if="errors">
                <b-col>
                    <ul class="text-danger">
                        <li v-for="error in errors">
                            {{error}}
                        </li>
                    </ul>
                </b-col>
            </b-row>

            <b-row>
                <b-col>
                    You can enter the info once and it will be applied to the entire submission. You can edit each item
                    individually afterwards.
                    <b-link class="text-dark" v-b-tooltip.hover
                            title="If every asset features the same model(s), you can choose for example Race, Ethnicity, Age, etc. one time">
                        <i class="fas fa-question-circle"/>
                    </b-link>
                </b-col>
            </b-row>

            <b-tabs class="mt-3">
                <b-tab title="Metadata">
                    <div class="font-size-sm font-weight-semi-bold mt-3 bg-light-blue p-1">CHOOSE TAGS</div>
                    <b-form-row class="mt-2">
                        <b-col md="12">
                            <tag-select field-name="Category (select up to 3)"
                                        :tags="categoriesOptions"
                                        tag-id-prop="value"
                                        tag-value-prop="text"
                                        :multiple="true"
                                        :select-picker-options="{maxOptions: 3}"
                                        v-model="selectedCategories"/>
                        </b-col>
                    </b-form-row>

                    <b-form-row>
                        <b-col md="6" v-for="(tagCategoryData, tagCategoryName) in tagsByCategory">
                            <tag-select :field-name="tagCategoryName"
                                        :tags="tagCategoryData.tags"
                                        :multiple="true"
                                        :select-picker-options="{maxOptions: tagCategoryData.max_options}"
                                        v-model="selectedTags[tagCategoryName]"/>
                        </b-col>
                    </b-form-row>

                    <b-form-row>
                        <b-col>
                            <div class="font-size-sm font-weight-semi-bold mt-3 bg-light-blue p-1">CREATE CUSTOM TAGS
                                (Separate Tags by comma, or hitting ENTER)
                            </div>
                            <b-form-group label="ADD KEYWORDS" class="mt-2">
                                <select2 v-if="shown"
                                         v-model="keywords"
                                         :settings="{ tags: true, multiple: true, placeholder: 'Type the keywords', tokenSeparators: [',', ';'] }"/>
                            </b-form-group>
                        </b-col>
                    </b-form-row>

                    <b-form-row>
                        <b-col>
                            <div class="font-size-sm font-weight-semi-bold mt-3 bg-light-blue p-1">
                                EXCLUSIVE CONTENT
                            </div>
                            <div class="d-flex mt-2">
                                <label class="mr-2 flex-grow-1 font-size-sm" style="flex-basis: 0">
                                    Will this content only be licensed for sale by pocstock?
                                </label>
                                <b-form-radio-group v-model="exclusive"
                                                    value-field="item"
                                                    text-field="name"
                                                    :options="exclusiveOptions"/>
                            </div>

                        </b-col>
                    </b-form-row>
                </b-tab>
                <b-tab title="Release Forms" v-if="releaseFormsNeeded">
                    <release-form-manager
                            class="mt-3"
                            ref="releaseFormManager"
                            :initial-release-forms="releaseForms"
                            :release-form-url="releaseFormUrl"/>
                </b-tab>
            </b-tabs>
        </b-modal>
    </div>
</template>

<script>
  import select2 from 'v-select2-component'
  import TagSelect from '../../Common/TagSelect'
  import ReleaseFormManager from '../../ReleaseForms/ReleaseFormManager'

  export default {

    props: ['tagsByCategory', 'categories', 'releaseForms', 'releaseFormsNeeded', 'releaseFormUrl'],

    data: function () {
      return {
        exclusive: null,
        selectedCategories: [],
        keywords: [],
        // create a blank selection object for tags by category, { cat1: [], cat2: [] .. )
        selectedTags: Object.keys(this.tagsByCategory).reduce((obj, key) => {
          obj[key] = [];
          return obj
        }, {}),

        // Modal v-model
        show: false,
        // Used to trigger select2 js once the modal is fully on screen
        shown: false,

        errors: []
      }
    },

    computed: {
      categoriesOptions: function () {
        return this.categories.map(category => {
          return {
            value: category.id,
            text: category.name
          }
        })
      },

      exclusiveOptions() {
        return [{item: true, name: 'YES'}, {item: false, name: 'NO'}]
      },

      assetFormData: function () {
        const selectedTags = Object.keys(this.selectedTags).reduce((array, key) => {
          return array.concat(this.selectedTags[key])
        }, []);

        const params = {
          category_ids: this.formDataArray(this.selectedCategories),
          keywords: this.formDataArray(this.keywords),
          tag_ids: this.formDataArray(selectedTags)
        };

        if (this.exclusive !== null) params.exclusive = this.exclusive;

        if (typeof this.$refs.releaseFormManager !== 'undefined') {
            const releaseForm = this.$refs.releaseFormManager.selectedReleaseForm;
            if (releaseForm) {
              params.release_form_ids = [releaseForm.id]
            }
        }

        return params;
      }
    },

    methods: {
      openModal: function () {
        this.show = true
      },

      formDataArray: function (array) {
        return Array.isArray(array) && array.length > 0 ? array : [''];
      },

      handleModalVisible: function () {
        this.shown = true;
      },

      handleSkip: function () {
        this.show = false;
        this.$emit('skip')
      },

      handleApply: function () {
        if (this.validate()) {
          this.show = false;
          this.$emit('submit', this.assetFormData)
        }
      },

      validate() {
        this.errors = [];
        if (this.exclusive === null) this.errors.push("Please select if this content will only be found on pocstock.com");
        if (this.selectedCategories.length === 0) this.errors.push("Please select at least one category")
        return this.errors.length === 0;
      }
    },

    components: {select2, TagSelect, ReleaseFormManager}
  }
</script>